import * as React from 'react';

import { useLazyQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';

import { useSystemProgram } from 'bundles/enterprise-admin-hooks/hooks/useSystemProgram';
import { useManageableGroups } from 'bundles/enterprise-admin-learners/hooks/useManageableGroups';
import PreviewImportAttributesGql from 'bundles/enterprise-admin-learners/queries/PreviewImportAttributes.graphql';
import type {
  PreviewImportAttributesQuery,
  PreviewImportAttributesQueryVariables,
} from 'bundles/enterprise-admin-learners/queries/__generated__/PreviewImportAttributes';
import ProgramInvitationsApiManager from 'bundles/enterprise-admin-program-users/utils/ProgramInvitationsApiManager';
import { useEnterpriseAdminPermissions } from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsProvider';
import { useThirdPartyOrganization } from 'bundles/enterprise-admin/ThirdPartyOrganizationProvider';

import _t from 'i18n!nls/enterprise-admin-learners';

type PreviewImportAttributesResult = NonNullable<
  NonNullable<PreviewImportAttributesQuery['BulkProgramInvitations']>['previewImportAttributes']
>;

export type CsvPreviewResult = {
  s3File: { bucket: string; key: string };
  groupColumnState: PreviewImportAttributesResult['groupColumnState'];
  shouldProcessAsync: boolean;
};

type CsvInvitePipelineStatus = 'idle' | 'processing' | 'success' | 'error';

type UseCsvInvitePipelineParams = {
  /** Set when the group is already known (Group Details entry, or a single-group Group admin). */
  autoSelectedGroupId?: string;
};

/**
 * Uploads a CSV to S3 (reusing the existing presigned-URL flow) and previews it via
 * `previewImportAttributes`, gating both behind a single "processing" status. When no group is
 * chosen yet, resolves an upload target for authorization: a Group admin isn't authorized against
 * the org's System Program, so falls back to one of their managed groups; an Org admin falls back
 * to the System Program itself — the same fallback `useAssignContentFromAllLearners` uses for
 * group-less actions.
 */
export const useCsvInvitePipeline = ({ autoSelectedGroupId }: UseCsvInvitePipelineParams) => {
  const org = useThirdPartyOrganization();
  const { systemProgram, loading: systemProgramLoading } = useSystemProgram(org.id);
  const { getIsOrganizationDomainAdmin } = useEnterpriseAdminPermissions();
  const isGroupAdmin = !getIsOrganizationDomainAdmin();
  const { manageableGroups } = useManageableGroups();
  const [file, setFile] = React.useState<File>();
  const [status, setStatus] = React.useState<CsvInvitePipelineStatus>('idle');
  const [error, setError] = React.useState('');
  const [preview, setPreview] = React.useState<CsvPreviewResult>();
  const [runPreviewImportAttributes] = useLazyQuery<
    PreviewImportAttributesQuery,
    PreviewImportAttributesQueryVariables
  >(PreviewImportAttributesGql, { context: { clientName: 'gatewayGql' } });

  let fallbackProgramId: string | undefined;
  if (isGroupAdmin) {
    fallbackProgramId = manageableGroups[0]?.id;
  } else {
    fallbackProgramId = systemProgram?.id;
  }
  const programId = autoSelectedGroupId ?? fallbackProgramId;
  const apiManager = React.useMemo(
    () =>
      programId
        ? new ProgramInvitationsApiManager({
            program: { id: programId },
            naptime: { executeMutation: () => undefined, refreshData: () => undefined },
          })
        : undefined,
    [programId]
  );

  const selectFile = (nextFile: File) => {
    setFile(nextFile);
    setStatus('idle');
    setError('');
  };

  const clearFile = () => {
    setFile(undefined);
    setStatus('idle');
    setError('');
  };

  const submit = async (): Promise<CsvPreviewResult | undefined> => {
    if (!file || !apiManager) {
      return undefined;
    }

    setStatus('processing');
    setError('');
    try {
      const { s3Location } = await apiManager.uploadFile(file);
      const { data, error: queryError } = await runPreviewImportAttributes({
        variables: { organizationId: org.id, s3Bucket: s3Location.bucket, s3Key: s3Location.key },
      });
      const result = data?.BulkProgramInvitations?.previewImportAttributes;
      if (queryError || !result) {
        throw new Error(queryError?.message ?? 'Empty preview result');
      }
      const previewResult: CsvPreviewResult = {
        s3File: s3Location,
        groupColumnState: result.groupColumnState,
        shouldProcessAsync: result.shouldProcessAsync,
      };
      setPreview(previewResult);
      setStatus('success');
      return previewResult;
    } catch (err) {
      Sentry.captureException(err);
      setError(_t("We couldn't process your CSV. Please try again."));
      setStatus('error');
      return undefined;
    }
  };

  const reset = React.useCallback<() => void>(() => {
    setFile(undefined);
    setStatus('idle');
    setError('');
    setPreview(undefined);
  }, []);

  // Ready immediately when the group is already known (Group Details entry, or a single-group
  // Group admin); a Group admin without one yet just needs a manageable group to resolve, an Org
  // admin waits for the System Program lookup.
  let programReady: boolean;
  if (autoSelectedGroupId) {
    programReady = true;
  } else if (isGroupAdmin) {
    programReady = Boolean(programId);
  } else {
    programReady = !systemProgramLoading && Boolean(programId);
  }

  return {
    file,
    status,
    error,
    preview,
    selectFile,
    clearFile,
    submit,
    reset,
    programReady,
    // The resolved upload/submission target — also the single source of truth for the final
    // ProcessImportSync/Async submission's fallback program id (CsvInviteModal), so it isn't
    // recomputed a second time with its own copy of the isGroupAdmin/manageableGroups branch.
    programId,
    isGroupAdmin,
  };
};
